import React from 'react';
import { Form, Select } from 'antd';

const { Option } = Select;

/** Dial prefix options: short code + dial in parentheses (e.g. IN (+91)), value for API, countryName for store address mapping. */
export const countryCodes = [
  { value: '+43', label: 'AT (+43)', countryName: 'Austria' },
  { value: '+61', label: 'AU (+61)', countryName: 'Australia' },
  { value: '+375', label: 'BY (+375)', countryName: 'Belarus' },
  { value: '+32', label: 'BE (+32)', countryName: 'Belgium' },
  { value: '+55', label: 'BR (+55)', countryName: 'Brazil' },
  { value: '+359', label: 'BG (+359)', countryName: 'Bulgaria' },
  { value: '+1-CA', label: 'CA (+1)', countryName: 'Canada' },
  { value: '+86', label: 'CN (+86)', countryName: 'China' },
  { value: '+385', label: 'HR (+385)', countryName: 'Croatia' },
  { value: '+420', label: 'CZ (+420)', countryName: 'Czech Republic' },
  { value: '+45', label: 'DK (+45)', countryName: 'Denmark' },
  { value: '+358', label: 'FI (+358)', countryName: 'Finland' },
  { value: '+33', label: 'FR (+33)', countryName: 'France' },
  { value: '+49', label: 'DE (+49)', countryName: 'Germany' },
  { value: '+30', label: 'GR (+30)', countryName: 'Greece' },
  { value: '+36', label: 'HU (+36)', countryName: 'Hungary' },
  { value: '+354', label: 'IS (+354)', countryName: 'Iceland' },
  { value: '+91', label: 'IN (+91)', countryName: 'India' },
  { value: '+62', label: 'ID (+62)', countryName: 'Indonesia' },
  { value: '+353', label: 'IE (+353)', countryName: 'Ireland' },
  { value: '+972', label: 'IL (+972)', countryName: 'Israel' },
  { value: '+39', label: 'IT (+39)', countryName: 'Italy' },
  { value: '+81', label: 'JP (+81)', countryName: 'Japan' },
  { value: '+371', label: 'LV (+371)', countryName: 'Latvia' },
  { value: '+370', label: 'LT (+370)', countryName: 'Lithuania' },
  { value: '+352', label: 'LU (+352)', countryName: 'Luxembourg' },
  { value: '+373', label: 'MD (+373)', countryName: 'Moldova' },
  { value: '+52', label: 'MX (+52)', countryName: 'Mexico' },
  { value: '+31', label: 'NL (+31)', countryName: 'Netherlands' },
  { value: '+64', label: 'NZ (+64)', countryName: 'New Zealand' },
  { value: '+47', label: 'NO (+47)', countryName: 'Norway' },
  { value: '+63', label: 'PH (+63)', countryName: 'Philippines' },
  { value: '+48', label: 'PL (+48)', countryName: 'Poland' },
  { value: '+40', label: 'RO (+40)', countryName: 'Romania' },
  { value: '+7', label: 'RU (+7)', countryName: 'Russia' },
  { value: '+381', label: 'RS (+381)', countryName: 'Serbia' },
  { value: '+65', label: 'SG (+65)', countryName: 'Singapore' },
  { value: '+421', label: 'SK (+421)', countryName: 'Slovakia' },
  { value: '+386', label: 'SI (+386)', countryName: 'Slovenia' },
  { value: '+27', label: 'ZA (+27)', countryName: 'South Africa' },
  { value: '+82', label: 'KR (+82)', countryName: 'South Korea' },
  { value: '+34', label: 'ES (+34)', countryName: 'Spain' },
  { value: '+46', label: 'SE (+46)', countryName: 'Sweden' },
  { value: '+41', label: 'CH (+41)', countryName: 'Switzerland' },
  { value: '+90', label: 'TR (+90)', countryName: 'Turkey' },
  { value: '+380', label: 'UA (+380)', countryName: 'Ukraine' },
  { value: '+44', label: 'GB (+44)', countryName: 'United Kingdom' },
  { value: '+1', label: 'US (+1)', countryName: 'United States' },
];

export const countryOptionsForAddress = (() => {
  const options = countryCodes.map((c) => ({
    label: c.countryName,
    value: c.countryName,
  }));
  const india = options.find((o) => o.value === 'India');
  const rest = options.filter((o) => o.value !== 'India');
  return india ? [india, ...rest] : options;
})();

export const getPhoneCodeForCountry = (countryName) => {
  if (!countryName) return '+91';
  const entry = countryCodes.find((c) => c.countryName === countryName);
  return entry ? entry.value : '+91';
};

/** Strip leading + from stored prefix value for API payloads (e.g. +91 -> 91, +1-CA -> 1-CA). */
export const stripLeadingPlusFromPrefix = (prefix) => String(prefix || '').replace(/^\+/, '');

/** Backend format: (isd)-(mobile) without + on ISD, e.g. 91-9776065643 */
export const mobileForApi = (prefix, digits) =>
  `${stripLeadingPlusFromPrefix(prefix)}-${String(digits || '').replace(/\D/g, '')}`;

/** Display only (e.g. User Name field): +91-9876543210 — keeps prefix as stored (+91, +1-CA, …). */
export const userNameDisplayFromPrefix = (prefix, digits) =>
  `${prefix || ''}-${String(digits || '').replace(/\D/g, '')}`;

export const MOBILE_MIN_LEN = 6;
export const MOBILE_MAX_LEN = 14;

/** Right padding so inline Verify button does not cover typed digits (Register / ResetPassword mobile + email). */
export const VERIFY_INPUT_PADDING_RIGHT = 100;

/** Login: no Verify button — do not reserve right padding (avoids “blank” zone hiding text). */
export const mobileFieldInputPlain = {
  width: '100%',
  minWidth: 0,
};

/** Register / ResetPassword mobile row: overlay Verify button needs padding. */
export const mobileFieldInputWithVerify = {
  width: '100%',
  minWidth: 0,
  paddingRight: VERIFY_INPUT_PADDING_RIGHT,
};

/** Full-width email row with same right padding for overlay verify button. */
export const emailVerifyInputStyle = {
  width: '100%',
  paddingRight: VERIFY_INPUT_PADDING_RIGHT,
};

export const isMobileLengthValid = (digits) => {
  const d = String(digits || '').replace(/\D/g, '');
  return d.length >= MOBILE_MIN_LEN && d.length <= MOBILE_MAX_LEN;
};

/**
 * Searchable country prefix Select — labels like IN (+91); search matches label, dial code, or country name.
 * Width is flexible (see authShared.module.css `.emailInputWrapper` for country + mobile row).
 */
export const PrefixFormItem = ({ name = 'prefix', initialValue = '+91', disabled = false }) => (
  <Form.Item name={name} noStyle initialValue={initialValue}>
    <Select
      className="phone-prefix-select"
      disabled={disabled}
      showSearch
      style={{ width: 'max-content', minWidth: '6.25rem' }}
      placeholder="Code"
      optionFilterProp="children"
      popupMatchSelectWidth={false}
      filterOption={(input, option) => {
        const q = input.trim().toLowerCase();
        if (!q) return true;
        const v = option?.value;
        const entry = countryCodes.find((c) => c.value === v);
        if (!entry) {
          const label = String(option?.children ?? '').toLowerCase();
          return label.includes(q);
        }
        const hay = `${entry.label} ${entry.countryName} ${entry.value}`.toLowerCase();
        return hay.includes(q);
      }}
    >
      {countryCodes.map((country) => (
        <Option key={country.value} value={country.value}>
          {country.label}
        </Option>
      ))}
    </Select>
  </Form.Item>
);
